import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import {
  GetAnalysisWidgetConfigResponse,
  PostAnalysisWidgetAnalyticsPayload,
  PostAnalysisWidgetDefaultConfigPayload,
  PostWidgetAnalysisConfigPayload,
  PostWidgetAnalysisResponse,
  PostWidgetDefaultConfigPayload,
  PostWidgetDefaultConfigResponse,
  PutAnalysisWidgetConfigPayload
} from "@/types/widget";
import { CustomError } from "@/utils/errors";

import {
  getAnalysisWidgetConfig,
  postAnalysisWidgetAnalyticsConfig,
  postAnalysisWidgetDefaultConfig,
  postWidgetAnalysisConfig,
  postWidgetDefaultConfig,
  putAnalysisWidgetConfig
} from "./widgetAPI";

export const useGetWidgetAnalysisConfig = (
  payload: PostWidgetAnalysisConfigPayload,
  options?: Omit<
    UseQueryOptions<PostWidgetAnalysisResponse, Error>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery({
    queryKey: ["widgetAnalysis", payload],
    queryFn: () => postWidgetAnalysisConfig(payload),
    ...options
  });

export const useMutateGetWidgetDefaultConfig = (
  options?: Omit<
    UseMutationOptions<
      PostWidgetDefaultConfigResponse,
      Error,
      PostWidgetDefaultConfigPayload
    >,
    "mutationKey" | "mutationFn"
  >
) =>
  useMutation({
    mutationKey: ["widgetDefaultConfig"],
    mutationFn: (
      body: PostWidgetDefaultConfigPayload,
      { meta }
    ): Promise<PostWidgetDefaultConfigResponse> =>
      meta?.isOnboarding
        ? Promise.resolve(
            meta.onboardingMockData as PostWidgetDefaultConfigResponse
          )
        : postWidgetDefaultConfig(body),
    ...options
  });

export const useQueryGetWidgetDefaultConfig = (
  payload: PostWidgetDefaultConfigPayload,
  options?: Omit<
    UseQueryOptions<PostWidgetDefaultConfigResponse, Error>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery({
    queryKey: ["widgetDefaultConfig", payload],
    queryFn: () => postWidgetDefaultConfig(payload),
    ...options
  });

export const useGetAnalysisWidgetAnalyticsConfig = (
  payload: PostAnalysisWidgetAnalyticsPayload,
  options?: Omit<
    UseQueryOptions<PostWidgetAnalysisResponse, CustomError>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery<PostWidgetAnalysisResponse, CustomError>({
    queryKey: ["analysisWidgetAnalytics", payload],
    queryFn: () => postAnalysisWidgetAnalyticsConfig(payload),
    ...options
  });

export const useMutateGetAnalysisWidgetDefaultConfig = (
  options?: Omit<
    UseMutationOptions<
      PostWidgetDefaultConfigResponse,
      Error,
      PostAnalysisWidgetDefaultConfigPayload
    >,
    "mutationKey" | "mutationFn"
  >
) =>
  useMutation({
    mutationKey: ["analysisWidgetDefaultConfig"],
    mutationFn: (body: PostAnalysisWidgetDefaultConfigPayload) =>
      postAnalysisWidgetDefaultConfig(body),
    ...options
  });

export const usePutAnalysisWidgetConfig = (
  options?: Omit<
    UseMutationOptions<
      unknown,
      Error,
      { analysisId: string; payload: PutAnalysisWidgetConfigPayload }
    >,
    "mutationFn"
  >
) => {
  return useMutation({
    mutationFn: async ({
      analysisId,
      payload
    }: {
      analysisId: string;
      payload: PutAnalysisWidgetConfigPayload;
    }) => putAnalysisWidgetConfig(analysisId, payload),
    meta: {
      invalidates: [["analysisWidgetConfig"]]
    },
    ...options
  });
};

export const useQueryAnalysisWidgetConfig = (
  analysisId: string,
  options?: Omit<
    UseQueryOptions<GetAnalysisWidgetConfigResponse, CustomError>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery<GetAnalysisWidgetConfigResponse, CustomError>({
    queryKey: ["analysisWidgetConfig", analysisId],
    queryFn: () => getAnalysisWidgetConfig(analysisId),
    ...options
  });
